import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Clock } from "lucide-react";
import { COMPANY } from "@/data/content";
import { useLocale } from "@/i18n/LocaleContext";
import LanguageSwitcher from "@/components/site/LanguageSwitcher";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t, path } = useLocale();
  const NAV = [
    { key: "home", label: t.nav.home },
    { key: "about", label: t.nav.about },
    { key: "services", label: t.nav.services },
    { key: "news", label: t.nav.news },
    { key: "contact", label: t.nav.contact },
  ].map((n) => ({ ...n, to: path(n.key) }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full bg-white/95 backdrop-blur-md border-b border-border transition-shadow duration-300 ${
        scrolled ? "shadow-[0_6px_24px_rgb(44,51,51,0.10)]" : "shadow-sm"
      }`}
    >
      {/* Premium Top-Bar */}
      <div className="hidden md:block bg-brand-deep text-white/90">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex items-center justify-between h-10 text-sm">
            <div className="flex items-center gap-5">
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-gold" /> {t.header.topbar24h}
              </span>
              <span className="text-white/30">·</span>
              <span className="hidden lg:inline text-white/70">{t.header.topbarConsult}</span>
            </div>
            <div className="flex items-center gap-5">
              <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-1.5 hover:text-gold transition-colors">
                <Phone className="w-3.5 h-3.5 text-gold" /> {COMPANY.phone}
              </a>
              <span className="text-white/30">·</span>
              <a href={`mailto:${COMPANY.email}`} className="hover:text-gold transition-colors">
                {COMPANY.email}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex items-center justify-between gap-4 h-20 xl:h-24">
          {/* Logo */}
          <Link
            to={path("home")}
            data-testid="header-logo"
            aria-label={COMPANY.name}
            className="flex items-center gap-3 rounded-xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/40"
          >
            <img
              src="/images/logo.png"
              alt={COMPANY.name}
              width="616"
              height="200"
              className="h-12 sm:h-14 xl:h-16 w-auto shrink-0"
            />
          </Link>

          {/* Desktop Nav */}
          <nav
            className="hidden xl:flex items-center gap-1"
            aria-label={t.header.mainNav}
          >
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.key}`}
                className={({ isActive }) =>
                  `whitespace-nowrap px-4 py-2 rounded-xl text-base font-600 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/40 ${
                    isActive
                      ? "text-brand bg-brand-light"
                      : "text-ink hover:text-brand hover:bg-brand-light/60"
                  }`
                }
                end={item.key === "home"}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Right: CTA (desktop) */}
          <div className="hidden xl:flex items-center gap-3">
            <LanguageSwitcher />
            <Link
              to={path("contact")}
              data-testid="header-cta"
              className="inline-flex shrink-0 whitespace-nowrap items-center gap-2 rounded-full bg-coral px-6 py-3 text-white font-700 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-coral-hover hover:shadow-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-coral/50"
            >
              <Phone className="w-4 h-4" /> {t.common.bookConsultation}
            </Link>
          </div>

          {/* Mobile controls */}
          <div className="flex items-center gap-2 xl:hidden">
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              data-testid="header-call-mobile"
              className="inline-flex items-center gap-2 rounded-xl bg-coral px-4 py-2.5 text-white font-600 text-base shadow-sm active:scale-95 transition-transform focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-coral/50"
            >
              <Phone className="w-5 h-5" strokeWidth={2} />
              {t.common.call}
            </a>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label={open ? t.header.closeMenu : t.header.openMenu}
              aria-expanded={open}
              className="inline-flex items-center justify-center w-11 h-11 rounded-xl border border-border text-ink hover:bg-brand-light transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/40"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <nav
          data-testid="mobile-nav"
          aria-label={t.header.mobileNav}
          className="xl:hidden border-t border-border bg-white px-5 pb-6 pt-2"
        >
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.key === "home"}
              data-testid={`mobile-nav-${item.key}`}
              className={({ isActive }) =>
                `block px-4 py-3.5 rounded-xl text-lg font-600 transition-colors ${
                  isActive
                    ? "text-brand bg-brand-light"
                    : "text-ink hover:bg-brand-light/60"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <LanguageSwitcher variant="mobile" />
          <div className="mt-4 rounded-2xl bg-brand-light p-4">
            <p className="flex items-center gap-2 text-sm text-brand-dark font-600">
              <Clock className="w-4 h-4" /> {t.common.reachable24h}
            </p>
            <a
              href={`tel:${COMPANY.emergencyRaw}`}
              className="mt-1 block font-serif text-xl font-600 text-brand-dark"
            >
              {COMPANY.emergency}
            </a>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
